import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bankApi, fmtCents, type BankTransaction } from "../lib/business";

export function BankPage() {
  const qc = useQueryClient();
  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ["bank-accounts"],
    queryFn: () => bankApi.listAccounts(),
  });
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);

  const accounts = accountsData?.accounts ?? [];
  const accountId = activeAccountId ?? accounts[0]?.id ?? null;

  const { data: txnData } = useQuery({
    queryKey: ["bank-txns"],
    queryFn: () => bankApi.listTransactions(),
    enabled: !!accountId,
  });

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Bank reconciliation</div>
          <h1 className="stage-title">
            Where every <em>rupee landed.</em>
          </h1>
          <p className="stage-sub">
            Upload a CSV (or PDF when fixtures land for your bank). The matching engine suggests
            invoice/bill matches; you approve in bulk by confidence band.
          </p>
        </div>
      </div>

      {loadingAccounts ? null : accounts.length === 0 ? (
        <NoAccountsState onCreated={() => qc.invalidateQueries({ queryKey: ["bank-accounts"] })} />
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {accounts.map((a) => (
              <button
                key={a.id}
                className={`nav-item ${accountId === a.id ? "active" : ""}`}
                onClick={() => setActiveAccountId(a.id)}
                style={{ background: accountId === a.id ? "var(--ink)" : "var(--paper-2)", border: "0.5px solid var(--line)" }}
              >
                {a.accountName} · {a.accountNumber.slice(-4)}
              </button>
            ))}
            <NewAccountInline onCreated={() => qc.invalidateQueries({ queryKey: ["bank-accounts"] })} />
          </div>

          <UploadSection
            accountId={accountId!}
            onUploaded={() => qc.invalidateQueries({ queryKey: ["bank-txns"] })}
          />

          <TransactionTable
            transactions={txnData?.transactions ?? []}
            onChanged={() => qc.invalidateQueries({ queryKey: ["bank-txns"] })}
          />
        </>
      )}
    </>
  );
}

function NoAccountsState({ onCreated }: { onCreated: () => void }) {
  return (
    <div className="card card-pad-lg" style={{ background: "var(--paper-2)", borderStyle: "dashed", textAlign: "center", padding: 60 }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, marginBottom: 10 }}>
        Add a bank account to start.
      </div>
      <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 20, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
        Tracks the account on your CoA and is required before uploading statements. You can add
        more later.
      </p>
      <NewAccountForm onCreated={onCreated} />
    </div>
  );
}

function NewAccountInline({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button className="btn btn-line" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setOpen(true)}>
        + Account
      </button>
    );
  }
  return (
    <div style={{ flex: 1, minWidth: 320 }}>
      <NewAccountForm
        onCreated={() => {
          onCreated();
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}

function NewAccountForm({ onCreated, onCancel }: { onCreated: () => void; onCancel?: () => void }) {
  const [bankCode, setBankCode] = useState("sampath");
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await bankApi.createAccount({ bankCode, accountName: name, accountNumber: number });
      setName("");
      setNumber("");
      onCreated();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr auto auto", gap: 8, alignItems: "end" }}>
      <div className="form-field">
        <label className="form-label">Bank</label>
        <select className="form-input" value={bankCode} onChange={(e) => setBankCode(e.target.value)}>
          <option value="sampath">Sampath</option>
          <option value="boc">BOC</option>
          <option value="hnb">HNB</option>
          <option value="commercial">Commercial</option>
          <option value="ndb">NDB</option>
          <option value="dfcc">DFCC</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="form-field">
        <label className="form-label">Account name</label>
        <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="form-field">
        <label className="form-label">Account number</label>
        <input className="form-input num" value={number} onChange={(e) => setNumber(e.target.value)} required />
      </div>
      <button className="btn btn-primary" type="submit" disabled={submitting}>
        {submitting ? "…" : "Add"}
      </button>
      {onCancel && (
        <button className="btn btn-ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
      )}
    </form>
  );
}

function UploadSection({ accountId, onUploaded }: { accountId: string; onUploaded: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function pick() {
    const f = inputRef.current?.files?.[0];
    if (!f) return;
    setUploading(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await bankApi.uploadStatement(accountId, f);
      setInfo(`Imported ${r.transactions} transactions (${r.parse_status}).`);
      onUploaded();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div className="stage-eyebrow">Upload</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
            Statement file
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4, maxWidth: 520 }}>
            CSV with <code>date, description, amount</code> works today. Sampath PDF parser ships
            once Keytiqa supplies sample fixtures.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input ref={inputRef} type="file" accept=".csv,.pdf,application/pdf,text/csv" onChange={pick} hidden />
          <button className="btn btn-primary" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : "Choose file"}
          </button>
        </div>
      </div>
      {info && <div style={{ marginTop: 14, padding: 12, background: "var(--accent-soft)", color: "var(--accent-deep)", borderRadius: 10, fontSize: 13 }}>{info}</div>}
      {err && <div className="banner banner-error" style={{ marginTop: 14 }}>{err}</div>}
    </div>
  );
}

function TransactionTable({
  transactions,
  onChanged,
}: {
  transactions: BankTransaction[];
  onChanged: () => void;
}) {
  const [filter, setFilter] = useState<string>("all");
  const filtered = transactions.filter((t) => {
    if (filter === "all") return true;
    if (filter === "to_review") return t.match_status !== "approved";
    return t.match_status === filter;
  });

  const greenCount = transactions.filter(
    (t) => t.match_status === "suggested" && parseFloat(t.match_confidence ?? "0") >= 99
  ).length;

  async function bulkApprove() {
    const r = await bankApi.bulkApprove(99);
    alert(`Approved ${r.approved} transactions.`);
    onChanged();
  }

  return (
    <div className="card card-pad-lg" style={{ padding: 0 }}>
      <div style={{ padding: 18, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "0.5px solid var(--line)" }}>
        <div className="bill-tabs" style={{ marginBottom: 0 }}>
          {[
            { k: "all", l: "All" },
            { k: "to_review", l: "To review" },
            { k: "suggested", l: "Suggested" },
            { k: "approved", l: "Approved" },
            { k: "unmatched", l: "Unmatched" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              className={`bill-tab ${filter === t.k ? "active" : ""}`}
              onClick={() => setFilter(t.k)}
              style={{
                padding: "7px 16px",
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 500,
                color: filter === t.k ? "var(--ink)" : "var(--ink-3)",
                background: filter === t.k ? "white" : "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t.l}
            </button>
          ))}
        </div>
        {greenCount > 0 && (
          <button className="btn btn-primary" onClick={bulkApprove}>
            Approve {greenCount} green-band →
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>
          No transactions in this view.
        </div>
      ) : (
        <div className="table-cards table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Date</th>
              <th style={th}>Description</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
              <th style={th}>Match</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => <TxnRow key={t.id} t={t} />)}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function TxnRow({ t }: { t: BankTransaction }) {
  const conf = t.match_confidence ? parseFloat(t.match_confidence) : null;
  let band = "—";
  let bandColor = "var(--ink-4)";
  if (t.match_status === "approved") {
    band = "Approved";
    bandColor = "var(--accent-deep)";
  } else if (conf && conf >= 99) {
    band = `Green · ${conf.toFixed(1)}%`;
    bandColor = "var(--accent)";
  } else if (conf && conf >= 95) {
    band = `Amber · ${conf.toFixed(1)}%`;
    bandColor = "var(--gold)";
  } else if (t.match_status === "unmatched") {
    band = "No match";
    bandColor = "var(--rose)";
  }
  const negative = BigInt(t.amount_cents) < 0n;
  return (
    <tr style={{ borderBottom: "0.5px solid var(--line)" }}>
      <td style={{ padding: "12px 18px", fontSize: 13, color: "var(--ink-3)" }} data-label="Date">
        {new Date(t.transaction_date).toLocaleDateString("en-LK", { day: "2-digit", month: "short" })}
      </td>
      <td style={{ padding: "12px 18px", fontSize: 13 }} data-label="Description">
        {t.description}
        {t.reference && <span style={{ color: "var(--ink-4)", marginLeft: 6 }}>· {t.reference}</span>}
      </td>
      <td
        style={{
          padding: "12px 18px",
          fontSize: 13,
          textAlign: "right",
          fontWeight: 500,
          color: negative ? "var(--rose)" : "var(--ink)",
        }}
        className="num"
        data-label="Amount"
      >
        {fmtCents(t.amount_cents)}
      </td>
      <td style={{ padding: "12px 18px", fontSize: 12, color: bandColor, fontWeight: 500 }} data-label="Match">
        {band}
      </td>
    </tr>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 18px",
  fontSize: 11,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-4)",
  fontWeight: 500,
  background: "var(--paper)",
  borderBottom: "0.5px solid var(--line)",
};
